import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getStudentEvidenceBundleV2,
  getTeacherSupportPlanV2,
  reviewSupportPlanV2,
  submitSupportPlanDraftV2,
} from '../lib/api';
import { useSchoolCapabilities, SCHOOL_ACTIONS } from '../lib/auth/capabilities';

function SectionList({ title, items }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-700">{title}</h4>
      {Array.isArray(items) && items.length > 0 ? (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
          {items.map((item, idx) => (
            <li key={`${title}-${idx}`}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-sm text-gray-500">No content yet.</p>
      )}
    </div>
  );
}

function EditableStringList({ title, items, onChange, placeholder }) {
  const list = Array.isArray(items) && items.length > 0 ? items : [''];

  const updateItem = (idx, value) => {
    onChange(list.map((item, i) => (i === idx ? value : item)));
  };

  const addItem = () => onChange([...list, '']);

  const removeItem = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    onChange(next.length > 0 ? next : ['']);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-700">{title}</h4>
      <div className="mt-2 space-y-2">
        {list.map((item, idx) => (
          <div key={`${title}-${idx}`} className="flex gap-2">
            <input
              type="text"
              value={item}
              onChange={(e) => updateItem(idx, e.target.value)}
              placeholder={placeholder}
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
            <button
              type="button"
              onClick={() => removeItem(idx)}
              className="rounded-md border border-gray-300 px-2 py-2 text-sm text-gray-600 hover:bg-gray-50"
              aria-label="Remove item"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addItem}
          className="text-sm font-medium text-amber-700 hover:text-amber-800"
        >
          + Add item
        </button>
      </div>
    </div>
  );
}

function planToEditForm(plan) {
  if (!plan) {
    return {
      keyClassroomStrategies: [''],
      strengths: [''],
      weaknesses: [''],
      identifiedNeed: [''],
      additionalContext: [''],
    };
  }
  const withDefault = (arr) => (Array.isArray(arr) && arr.length > 0 ? [...arr] : ['']);
  return {
    keyClassroomStrategies: withDefault(plan.keyClassroomStrategies),
    strengths: withDefault(plan.strengthsAndWeaknesses?.strengths),
    weaknesses: withDefault(plan.strengthsAndWeaknesses?.weaknesses),
    identifiedNeed: withDefault(plan.identifiedNeed),
    additionalContext: withDefault(plan.additionalContext),
  };
}

function trimList(items) {
  return (items || []).map((item) => String(item || '').trim()).filter(Boolean);
}

function editFormToSections(form) {
  return {
    keyClassroomStrategies: trimList(form.keyClassroomStrategies),
    strengthsAndWeaknesses: {
      strengths: trimList(form.strengths),
      weaknesses: trimList(form.weaknesses),
    },
    identifiedNeed: trimList(form.identifiedNeed),
    additionalContext: trimList(form.additionalContext),
  };
}

export default function SupportPlanApprovalPanel({ studentId }) {
  const { can } = useSchoolCapabilities();
  const canApprove = can(SCHOOL_ACTIONS.APPROVE_APDR_QUEUE);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('draft_generated');
  const [plan, setPlan] = useState(null);
  const [draftSections, setDraftSections] = useState(null);
  const [busy, setBusy] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(() => planToEditForm(null));

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const [teacherPlan, evidence] = await Promise.all([
        getTeacherSupportPlanV2({ studentId }),
        getStudentEvidenceBundleV2({ studentId }),
      ]);
      setStatus(teacherPlan?.status || 'draft_generated');
      setPlan(teacherPlan?.plan || null);
      setDraftSections(evidence?.draftPlanSections || null);
    } catch (error) {
      console.error('Error loading support plan panel:', error);
      toast.error('Failed to load support plan');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleGenerateDraft = async () => {
    try {
      setBusy(true);
      await submitSupportPlanDraftV2({
        studentId,
        sections: draftSections || undefined,
        changeSummary: 'Draft refreshed from unified evidence bundle',
      });
      toast.success('Draft submitted for SENCO review');
      setIsEditing(false);
      await load();
    } catch (error) {
      console.error('Error generating support plan draft:', error);
      toast.error('Could not submit draft');
    } finally {
      setBusy(false);
    }
  };

  const handleStartEdit = () => {
    setEditForm(planToEditForm(plan || draftSections));
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm(planToEditForm(plan || draftSections));
  };

  const handleSaveDraft = async () => {
    const sections = editFormToSections(editForm);
    if (
      sections.keyClassroomStrategies.length === 0 &&
      sections.strengthsAndWeaknesses.strengths.length === 0 &&
      sections.strengthsAndWeaknesses.weaknesses.length === 0 &&
      sections.identifiedNeed.length === 0 &&
      sections.additionalContext.length === 0
    ) {
      toast.error('Add at least one item before saving');
      return;
    }
    try {
      setBusy(true);
      await submitSupportPlanDraftV2({
        studentId,
        sections,
        changeSummary: 'SENCO edited classroom strategies draft',
      });
      toast.success('Draft saved');
      setIsEditing(false);
      await load();
    } catch (error) {
      console.error('Error saving support plan draft:', error);
      toast.error('Could not save draft');
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async () => {
    try {
      setBusy(true);
      await reviewSupportPlanV2({ studentId, action: 'approve' });
      toast.success('Plan approved and published');
      setIsEditing(false);
      await load();
    } catch (error) {
      console.error('Error reviewing support plan:', error);
      toast.error('Could not update approval state');
    } finally {
      setBusy(false);
    }
  };

  const visiblePlan = plan || draftSections || null;

  return (
    <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Classroom strategies: SENCO review</h3>
          <p className="text-sm text-gray-600">
            Professional reports and evidence feed this section. Approved content appears on the one-page profile and ISP PDF.
            Teacher view is gated until SENCO approval. Current state:{' '}
            <span className="font-medium">{status}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={handleGenerateDraft}
          disabled={busy}
          className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
        >
          Regenerate Draft from Evidence
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading support plan evidence...</p>
      ) : isEditing ? (
        <div className="grid gap-3">
          <EditableStringList
            title="1) Key classroom strategies"
            items={editForm.keyClassroomStrategies}
            onChange={(items) => setEditForm((prev) => ({ ...prev, keyClassroomStrategies: items }))}
            placeholder="e.g. Use visual timetable for transitions"
          />
          <EditableStringList
            title="2) Strengths"
            items={editForm.strengths}
            onChange={(items) => setEditForm((prev) => ({ ...prev, strengths: items }))}
            placeholder="e.g. Strong verbal reasoning"
          />
          <EditableStringList
            title="2) Needs support with"
            items={editForm.weaknesses}
            onChange={(items) => setEditForm((prev) => ({ ...prev, weaknesses: items }))}
            placeholder="e.g. Sustaining attention in group tasks"
          />
          <EditableStringList
            title="3) Identified need"
            items={editForm.identifiedNeed}
            onChange={(items) => setEditForm((prev) => ({ ...prev, identifiedNeed: items }))}
            placeholder="e.g. Communication and interaction"
          />
          <EditableStringList
            title="4) Additional context"
            items={editForm.additionalContext}
            onChange={(items) => setEditForm((prev) => ({ ...prev, additionalContext: items }))}
            placeholder="e.g. EHCP review due Spring term"
          />
        </div>
      ) : visiblePlan ? (
        <div className="grid gap-3">
          <SectionList title="1) Key classroom strategies" items={visiblePlan.keyClassroomStrategies || []} />
          <SectionList
            title="2) Strengths and weaknesses"
            items={[
              ...(visiblePlan?.strengthsAndWeaknesses?.strengths || []).map((v) => `Strength: ${v}`),
              ...(visiblePlan?.strengthsAndWeaknesses?.weaknesses || []).map((v) => `Need support with: ${v}`),
            ]}
          />
          <SectionList title="3) Identified need" items={visiblePlan.identifiedNeed || []} />
          <SectionList title="4) Additional context" items={visiblePlan.additionalContext || []} />
        </div>
      ) : (
        <p className="text-sm text-gray-500">No plan is available yet.</p>
      )}

      {canApprove && status === 'pending_senco_review' && (
        <div className="flex flex-wrap gap-2">
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={busy}
                className="rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60"
              >
                Save draft
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={busy}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleApprove}
                disabled={busy}
                className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                Approve and Publish for Teachers
              </button>
              <button
                type="button"
                onClick={handleStartEdit}
                disabled={busy}
                className="rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60"
              >
                Edit
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
